import { useId, useState } from "react"

import { Box, Tab, Tabs } from "@mui/material"
import { useQueryAdminDashboardBehaviors } from "api/reactQuery/queries/adminDashboard"
import Dialog from "components/common/dialog/dialog"
import Pagination from "components/common/pagination"
import { useTranslation } from "react-i18next"
import {
  IDashboardBehaviorSelection,
  IDashboardPeriodParams,
} from "ts/interfaces/AdminDashboard"
import { DashboardBehaviorType } from "ts/types/AdminDashboard"

import BehaviorList from "./BehaviorList"
import DashboardSectionBody, {
  RowsSkeleton,
  useShownValue,
} from "./DashboardSectionBody"

const PAGE_SIZE = 20
const TYPES: DashboardBehaviorType[] = ["bonus", "fine"]

const getPageCount = (
  total: number | null | undefined,
  page: number,
  rows: number,
) => {
  if (total === null) return rows === PAGE_SIZE ? page + 1 : page
  return Math.ceil((total ?? 0) / PAGE_SIZE)
}

const BehaviorsFullView = ({
  params,
  type,
  onTypeChange,
  onClose,
  selection,
  onSelect,
}: {
  params: IDashboardPeriodParams
  type: DashboardBehaviorType | null
  onTypeChange: (type: DashboardBehaviorType) => void
  onClose: () => void
  selection: IDashboardBehaviorSelection | null
  onSelect: (behavior: IDashboardBehaviorSelection) => void
}) => {
  const { t } = useTranslation()
  const tabsId = useId()
  const [page, setPage] = useState(1)
  const activeType = type ?? "bonus"
  const query = useQueryAdminDashboardBehaviors(
    params,
    activeType,
    { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
    { enabled: type !== null },
  )
  const shownPage = useShownValue(page, query.isPreviousData)
  const shownType = useShownValue(activeType, query.isPreviousData)
  const rows = query.data?.data.objects ?? []
  const total = query.data?.data.all_objects_count
  const pageCount = getPageCount(total, shownPage, rows.length)
  const from = (shownPage - 1) * PAGE_SIZE + 1
  const to = from + rows.length - 1
  const kind = shownType === "bonus" ? "Bonuses" : "Fines"
  const summary = !rows.length
    ? undefined
    : typeof total === "number"
      ? t(`adminDashboard.showing${kind}`, { from, to, total })
      : t(`adminDashboard.showing${kind}NoTotal`, { from, to })

  const changeType = (next: DashboardBehaviorType) => {
    setPage(1)
    onTypeChange(next)
  }

  return (
    <Dialog
      open={type !== null}
      onClose={onClose}
      fullScreen
      titleText={t("adminDashboard.allBehaviours")}
      customActionButtonsSlot={null}
      sx={(theme) => ({
        "& .MuiDialog-paper": {
          [theme.breakpoints.up("tablet")]: { maxWidth: "none" },
          [theme.breakpoints.up("desktop")]: { maxWidth: "none" },
        },
      })}
      contentWrapperProps={{ sx: { width: "100%", maxWidth: 960, mx: "auto" } }}
    >
      <Tabs
        value={activeType}
        onChange={(_, next: DashboardBehaviorType) => changeType(next)}
      >
        {TYPES.map((value) => (
          <Tab
            key={value}
            value={value}
            id={`${tabsId}-${value}-tab`}
            aria-controls={`${tabsId}-${value}-panel`}
            label={t(
              value === "bonus"
                ? "adminDashboard.bonuses"
                : "adminDashboard.fines",
            )}
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={`${tabsId}-${activeType}-panel`}
        aria-labelledby={`${tabsId}-${activeType}-tab`}
        sx={{ mx: -3 }}
      >
        <DashboardSectionBody
          queries={[query]}
          skeleton={<RowsSkeleton rows={PAGE_SIZE} />}
        >
          {/* The rows may be the previous type's, so a press selects the shown one */}
          <BehaviorList
            type={shownType}
            rows={rows}
            startRank={from}
            selection={selection}
            onSelect={onSelect}
          />
        </DashboardSectionBody>
      </Box>
      <Pagination
        page={page}
        count={pageCount}
        onChange={setPage}
        summary={summary}
      />
    </Dialog>
  )
}

export default BehaviorsFullView
